import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import get from 'lodash/get';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import Group from '@lightspeed/cirrus-group';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '@lightspeed/cirrus-modal';

import {
  TERMINAL_TYPE,
  TERMINAL_ATTRIBUTES,
  TERMINAL_PAIRING_METHOD,
} from 'shared_modules/Terminals';

import AddForm from './Form';
import validationSchema from './Form/validations';

export const mapConfiguration = (terminalModel, pairingInput) => {
  // Apply temporary transformer for Adyen until CLD-33034 is completed,
  switch (TERMINAL_ATTRIBUTES[terminalModel]?.pairingMethod) {
    case TERMINAL_PAIRING_METHOD.SERIAL_NUMBER:
      return { serialNumber: pairingInput.replace(/-/g, '') };
    case TERMINAL_PAIRING_METHOD.ACTIVATION_CODE:
    default:
      return { activationCode: pairingInput };
  }
};

const AddCCTerminalComponent = ({
  isOpen,
  isProcessing,
  onCancel,
  gatewayConfigID,
  onSuccess,
  supportedTerminalTypes,
  ccTerminalErrors = [],
}) => {
  const apiErrors = ccTerminalErrors.map(apiError => ({
    errorCode: get(apiError, 'response.data.errorCode', ''),
    errorMessage: get(apiError, 'response.data.message', ''),
  }));

  const initialValues = {
    gatewayConfigID,
    name: '',
    terminalModel: supportedTerminalTypes ? supportedTerminalTypes[0] : '',
    pairingInput: '',
  };

  return (
    <Modal
      className="cirrus"
      isOpen={isOpen}
      onRequestClose={onCancel}
      shouldCloseOnOverlayClick={!isProcessing}
      closeOnEsc={!isProcessing}
    >
      <ModalHeader showCloseButton={false}>Add a Payment Terminal</ModalHeader>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={({ pairingInput, name, terminalModel }) => {
          // All WP terminals use the same 'type' but we need to differentiate in the front-end between them
          // for selection and display.  So, here, we need to adapt our internal-only terminal types to the
          // correct value to use for the API.
          const model =
            terminalModel === TERMINAL_TYPE.LIGHTSPEED_IPP320_VANTIV ||
            terminalModel === TERMINAL_TYPE.LIGHTSPEED_ISMP4_VANTIV
              ? TERMINAL_TYPE.LIGHTSPEED_MX915_VANTIV
              : terminalModel;

          const mappedPayload = {
            configuration: mapConfiguration(terminalModel, pairingInput),
            createTime: '',
            gatewayConfigID,
            name: name.trim(),
            shopID: 0,
            timeStamp: '',
            type: supportedTerminalTypes ? model : TERMINAL_TYPE.LIGHTSPEED,
          };
          onSuccess(mappedPayload);
        }}
        validationSchema={validationSchema}
        render={({ isValid, handleSubmit }) => (
          <form>
            <ModalBody>
              <div style={{ width: '400px' }}>
                {apiErrors.length > 0 && (
                  <Alert type="danger" mb="4" title="Unable to add your terminal">
                    {apiErrors.map(({ errorMessage }) => (
                      <p style={{ overflowWrap: 'anywhere' }}>{errorMessage}</p>
                    ))}
                  </Alert>
                )}

                <AddForm supportedTerminalTypes={supportedTerminalTypes} />
              </div>
            </ModalBody>
            <ModalFooter>
              <Group className="cr-justify-end">
                <Button
                  onClick={onCancel}
                  disabled={isProcessing}
                  data-test="retail-payments-ccterminal-cancel"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  onClick={handleSubmit}
                  loading={isProcessing}
                  disabled={!isValid}
                  data-test="retail-payments-ccterminal-save"
                  fill
                >
                  Add
                </Button>
              </Group>
            </ModalFooter>
          </form>
        )}
      />
    </Modal>
  );
};

AddCCTerminalComponent.propTypes = {
  isOpen: PropTypes.bool,
  onSuccess: PropTypes.func,
  isProcessing: PropTypes.bool,
  ccTerminalErrors: PropTypes.arrayOf(PropTypes.shape(PropTypes.any.object)),
  supportedTerminalTypes: PropTypes.arrayOf(PropTypes.string),
};

export default AddCCTerminalComponent;
